import * as React from 'react';
import { cn } from '@/lib/utils';
import DefaultBanner from '@/assets/images/canaleducacao/Boas_vindas_CanalEducacao.png';
import EgepiBanner from '@/assets/images/egepi/EgepiBanner.png';
import { Button } from '@/components/ui/button';
import useHeroisEnemLink from '@/hooks/useHeroisEnemLink';

// Só a página inicial do domínio exato identifica a integração.
function isHeroisEnemLink(link) {
  try {
    const url = new URL(link);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname === 'heroisdoenem.com' && !url.port && !url.username && !url.password && url.pathname === '/' && !url.search && !url.hash;
  } catch {
    return false;
  }
}

function WarningWallAction({ warning, className }) {
  const { canAccess, open, loading } = useHeroisEnemLink();

  if (isHeroisEnemLink(warning.link) && canAccess) {
    return (
      <Button
        variant="outline"
        wide
        className={className}
        type="button"
        onClick={open}
        disabled={loading}
        aria-busy={loading}
      >
        {warning.label || 'Acessar'}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      wide
      className={className}
      asChild
    >
      <a
        href={warning.link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {warning.label || 'Acessar'}
      </a>
    </Button>
  );
}

const WarningWallImg = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('c-warning-wall-img', 'h-[260px] w-full shrink-0 grow object-cover xl:w-[280px]', className)}
    {...props}
  />
));
WarningWallImg.displayName = 'WarningWallImg';

const WarningWallContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-warning-wall-content', 'flex grow flex-col gap-2 bg-white p-6', className)}
    {...props}
  />
));
WarningWallContent.displayName = 'WarningWallContent';

const WarningWallHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-warning-wall-header', '', className)}
    {...props}
  />
));
WarningWallHeader.displayName = 'WarningWallHeader';

const WarningWallBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-warning-wall-body', 'grow', className)}
    {...props}
  />
));
WarningWallBody.displayName = 'WarningWallBody';

const WarningWallFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-warning-wall-footer', '', className)}
    {...props}
  />
));
WarningWallFooter.displayName = 'WarningWallFooter';

const WarningWallTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-warning-wall-title', 'mb-0 line-clamp-2 max-h-[76px] text-3xl font-bold text-primary', className)}
    {...props}
  />
));
WarningWallTitle.displayName = 'WarningWallTitle';

const WarningWallDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-warning-wall-description', 'mb-0 line-clamp-3 text-[1.125rem] font-light text-neutral-dark', className)}
    {...props}
  />
));
WarningWallDescription.displayName = 'WarningWallDescription';

const WarningWall = React.forwardRef(({ className, warning, ...props }, ref) => {
  const appName = process.env.REACT_APP_NAME;

  const imageSource = React.useMemo(() => {
    if (warning) {
      if (warning?.image) {
        return warning?.image.url || DefaultBanner;
      }
      return DefaultBanner;
    } else {
      if (appName === 'egepi') {
        return EgepiBanner;
      } else {
        return DefaultBanner;
      }
    }
  }, [warning]);

  // Modo overlay: banners marcados com text-overlay no admin têm arte de fundo
  // limpa e o texto (title/description) é renderizado em HTML sobre a imagem —
  // responsivo de verdade, nunca corta. Banners legados (texto rasterizado na
  // arte) têm text-overlay=false e caem no modo imagem pura abaixo.
  const hasCustomImage = Boolean(warning?.image);
  const showOverlay = hasCustomImage && Boolean(warning?.['text-overlay']) && Boolean(warning?.title);
  const showContent = !hasCustomImage && Boolean(warning?.title) && Boolean(warning?.description);

  return (
    <div
      ref={ref}
      className={cn('c-warning-wall', 'relative mb-10 overflow-hidden rounded-xl border border-solid border-neutral-lightest bg-[#FAFAFA] shadow-dropshadow-lg', showContent && 'flex min-h-[260px] flex-col xl:flex-row', className)}
      {...props}
    >
      {showOverlay && (
        <div className="relative flex min-h-[220px] flex-col justify-center xl:min-h-[260px]">
          {/* Arte de fundo decorativa (o texto real está no HTML abaixo) —
              object-cover pode cropar a arte sem perda de informação. */}
          <WarningWallImg
            src={imageSource}
            alt=""
            className="absolute inset-0 h-full w-full xl:w-full"
          />
          {/* Scrim: garante contraste do texto sobre qualquer arte. */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-transparent" />

          <div className="relative flex max-w-[620px] flex-col items-start gap-2 p-6 md:p-10">
            <WarningWallTitle className="max-h-none text-2xl leading-tight text-white md:text-3xl">{warning.title}</WarningWallTitle>

            {warning?.description && <WarningWallDescription className="text-white/90">{warning.description}</WarningWallDescription>}

            {warning?.link && (
              <WarningWallAction
                warning={warning}
                className="mt-2 border-white bg-white/15 text-white hover:bg-white hover:text-primary"
              />
            )}
          </div>
        </div>
      )}

      {!showOverlay && (
        <WarningWallImg
          src={imageSource}
          alt={warning?.title || 'imagem do aviso'}
          className={cn(
            !showContent &&
              (warning
                ? // Sem overlay: a arte carrega o próprio texto. Altura fluida
                  // na proporção oficial do upload (1020x252, hint do admin) em
                  // vez de h-[260px] fixo — o object-cover não cropa mais as
                  // laterais (e o texto embutido) conforme a largura da tela
                  // muda.
                  'aspect-[1020/252] h-auto xl:w-full'
                : // Sem nenhum warning cadastrado (fallback): mesma altura do
                  // modo overlay (min-h-[220px]/xl:min-h-[260px]) em vez da
                  // proporção acima — senão a arte padrão sai maior que os
                  // banners reais.
                  'h-[220px] w-full xl:h-[260px] xl:w-full')
          )}
        />
      )}

      {!showOverlay && warning?.link && warning?.image && (
        <WarningWallAction
          warning={warning}
          className="absolute bottom-3 right-6"
        />
      )}

      {showContent && (
        <WarningWallContent>
          <WarningWallHeader>
            <WarningWallTitle title={warning?.title}>{warning?.title}</WarningWallTitle>
          </WarningWallHeader>

          <WarningWallBody>
            <WarningWallDescription title={warning?.description}>{warning?.description}</WarningWallDescription>
          </WarningWallBody>

          {warning?.link && (
            <WarningWallFooter>
              <WarningWallAction warning={warning} />
            </WarningWallFooter>
          )}
        </WarningWallContent>
      )}
    </div>
  );
});
WarningWall.displayName = 'WarningWall';

export { WarningWall };
